import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import {
  type AuthData,
  initialAuthData,
} from './auth.store.internals';
import { type AuthActions, createActions } from './auth.store.actions';

export type AuthStore = AuthData & AuthActions;

// Public name retained for backwards-compatibility with `auth-store-events.ts`
// and any external `import type { AuthState }` consumers. Internally the
// store speaks in terms of `AuthStore`.
export type AuthState = AuthStore;

/* ============================================================================
 * AUTH STORE
 * ============================================================================
 *
 * Two real files:
 *
 *   • `auth.store.internals.ts` — `AuthData` shape, `initialAuthData`, and
 *     User-cache helpers (`setCachedUser` / `getCachedUser` / identity-change
 *     wipe). The User entity itself is NOT a field on this store — it lives
 *     in the TanStack Query cache (`cacheKeys.user`) per STATE_POLICY.
 *
 *   • `auth.store.actions.ts` — every async pipeline (login/register/logout/
 *     refresh/oauth/...) plus `clearErrors` and `reset`. Lives in its own
 *     file because the action bodies dwarf the data slice — keeping them
 *     out of this entry point lets the store stay a thin composition root.
 *
 * The earlier 3-slice split (identity / flow / actions) was tried and
 * discarded: actions freely write into both Identity and Flow fields in the
 * same `set()` call, so the boundary was fictional.
 * ==========================================================================*/
export const useAuthStore = create<AuthStore>()(
  subscribeWithSelector((...a) => ({
    ...initialAuthData,
    ...createActions(...a),
  }))
);
